import APIConfiguration from '@savgroup-front-common/configuration/api';
import {
  Exception,
  logError,
} from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { currentAppEnvironment } from '@savgroup-front-common/constants/shared';

import {
  BuildTranslationUrlArgs,
  FALLBACK_TRANSLATIONS_DIRECTORY,
  FetchTranslationsArgs,
  IntlManagerResponse,
  NEW_TRANSLATIONS_DIRECTORY,
  NEW_TRANSLATIONS_ENABLED_ENVS,
  TRANSLATION_SCOPE,
  TRANSLATIONS_DIRECTORY,
} from './IntlManager.types';

const translationBase = window.location.hostname.includes('localhost')
  ? APIConfiguration.translationCDN
  : '/';

const translationsDirectory = NEW_TRANSLATIONS_ENABLED_ENVS.includes(
  currentAppEnvironment,
)
  ? NEW_TRANSLATIONS_DIRECTORY
  : TRANSLATIONS_DIRECTORY;

const buildTranslationUrl = ({
  directory,
  scope,
  locale,
}: BuildTranslationUrlArgs): string =>
  `${translationBase}${directory}/${scope}${locale}.json`;

async function fetchTranslationFile(url: string): Promise<IntlManagerResponse> {
  try {
    const response = await fetch(url);

    if (!response.ok) {
      logError(
        new Error(
          `Unable to fetch translations: ${response.status} ${response.statusText}`,
        ),
        { url, status: response.status, statusText: response.statusText },
      );

      return { failure: true };
    }

    return { failure: false, value: await response.json() };
  } catch (e) {
    logError(e as Exception, { url });

    return { failure: true };
  }
}

async function fetchTranslations({
  scope,
  locale,
}: FetchTranslationsArgs): Promise<IntlManagerResponse> {
  const response = await fetchTranslationFile(
    buildTranslationUrl({ directory: translationsDirectory, scope, locale }),
  );

  if (!response.failure) {
    return response;
  }

  return fetchTranslationFile(
    buildTranslationUrl({
      directory: FALLBACK_TRANSLATIONS_DIRECTORY,
      scope,
      locale,
    }),
  );
}

export const fetchIntl = (locale: string): Promise<IntlManagerResponse> =>
  fetchTranslations({ scope: TRANSLATION_SCOPE.STATIC, locale });

export const fetchDynamicIntl = (
  locale: string,
): Promise<IntlManagerResponse> =>
  fetchTranslations({ scope: TRANSLATION_SCOPE.DYNAMIC, locale });
